import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { PrivacyNotice } from "./PrivacyNotice";
import {
  ArrowRight, ArrowLeft, Check, AlertTriangle, ChevronDown, ChevronUp,
  Send, Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  DS_HERO, DS_STATS, DS_PROBLEM, DS_FABRIC, DS_USECASES,
  DS_METHODOLOGY, DS_GOVERNANCE, DS_ROI, DS_OUTCOMES, DS_JOURNEY, DS_FAQ
} from "../data/dataScienceData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ id, bg = "bg-white", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${bg}`}>
    <div className="max-w-7xl mx-auto px-6">{children}</div>
  </section>
);

/* Hero */
const DSHero = () => (
  <section className="relative min-h-[85vh] flex flex-col justify-center bg-[#273519] overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#7EA13E]/10 rounded-full -translate-y-1/3 translate-x-1/3" />
    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E5BD77]/[0.05] rounded-full translate-y-1/3 -translate-x-1/4" />
    <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24 w-full">
      <div className="flex items-center justify-between mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#A9B294] hover:text-[#E5BD77] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <div className="inline-flex items-center gap-2 bg-[#7EA13E]/20 text-[#c4e08a] text-sm font-semibold px-4 py-1.5 rounded-full">
          <span className="w-2 h-2 bg-[#7EA13E] rounded-full animate-pulse" />
          {DS_HERO.eyebrow}
        </div>
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-2 max-w-4xl">
        {DS_HERO.headline}
      </h1>
      <p className="text-2xl lg:text-3xl font-bold text-[#7EA13E] mb-5">{DS_HERO.headlineSuffix}</p>
      <p className="text-lg lg:text-xl text-[#b5c7a0] leading-relaxed max-w-2xl mb-10">{DS_HERO.subheadline}</p>
      <div className="flex flex-col sm:flex-row gap-4 mb-16">
        <a href={DS_HERO.primaryCTA.href}>
          <Button className="bg-[#7EA13E] hover:bg-[#6b8a34] text-white font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#7EA13E]/20 hover:-translate-y-0.5 group">
            {DS_HERO.primaryCTA.label}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </a>
        <a href={DS_HERO.secondaryCTA.href}>
          <Button variant="outline" className="border-2 border-white/20 text-white hover:border-[#E5BD77] hover:text-[#E5BD77] font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300">
            {DS_HERO.secondaryCTA.label}
          </Button>
        </a>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {DS_STATS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.08] rounded-xl px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#7EA13E]/20 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#c4e08a]" />
              </div>
              <div>
                <div className="text-lg font-bold text-white">{s.value}</div>
                <div className="text-xs text-[#a0b18a] font-medium">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* Problem */
const DSProblem = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">The Challenge</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{DS_PROBLEM.headline}</h2>
        <p className="text-[#6B6567] text-lg leading-relaxed mb-6">{DS_PROBLEM.description}</p>
        <p className="text-[#7EA13E] font-semibold text-base italic">"{DS_PROBLEM.solution}"</p>
      </div>
      <div className="mt-10 lg:mt-0">
        <div className="bg-white rounded-2xl border border-[#E0D1BB]/30 p-8">
          <h3 className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-5">Traditional Data Science Pitfalls</h3>
          <div className="space-y-3">
            {DS_PROBLEM.painPoints.map((p, i) => (
              <div key={i} className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-[#E5BD77] shrink-0" />
                <span className="text-sm text-[#494546]">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* Fabric Platform */
const DSFabric = () => (
  <Section bg="bg-white">
    <div className="max-w-3xl mx-auto text-center mb-12">
      <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Platform</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{DS_FABRIC.headline}</h2>
      <p className="text-[#6B6567] text-lg leading-relaxed">{DS_FABRIC.description}</p>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-3xl mx-auto mb-8">
      {DS_FABRIC.benefits.map((b, i) => {
        const Icon = b.icon;
        return (
          <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            <div className="w-14 h-14 rounded-xl bg-[#7EA13E]/[0.08] flex items-center justify-center mx-auto mb-4">
              <Icon className="w-7 h-7 text-[#7EA13E]" />
            </div>
            <p className="text-sm font-semibold text-[#2C2A2B]">{b.text}</p>
          </div>
        );
      })}
    </div>
    <p className="text-center text-[#308795] font-semibold text-base">{DS_FABRIC.conclusion}</p>
  </Section>
);

/* Use Cases */
const DSUseCases = () => (
  <Section id="ds-usecases" bg="bg-[#FAFAF7]">
    <div className="max-w-3xl mb-14">
      <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Capabilities</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">What This Means for SMEs</h2>
      <p className="text-[#6B6567] text-lg">Through Microsoft Fabric, we help organisations unlock intelligence across every function.</p>
    </div>
    <div className="space-y-5">
      {DS_USECASES.map((uc, i) => {
        const Icon = uc.icon;
        return (
          <div key={i} className="group bg-white border border-[#E0D1BB]/30 rounded-2xl p-6 lg:p-8 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            <div className="lg:grid lg:grid-cols-5 lg:gap-8 items-start">
              <div className="lg:col-span-2 mb-4 lg:mb-0">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#7EA13E]/[0.08] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#7EA13E]" />
                  </div>
                  <span className="text-xs font-bold text-[#7EA13E] uppercase tracking-wider">Use Case {i + 1}</span>
                </div>
                <h3 className="text-xl font-bold text-[#2C2A2B] mb-2">{uc.title}</h3>
                <p className="text-sm text-[#6B6567] leading-relaxed">{uc.description}</p>
              </div>
              <div className="lg:col-span-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {uc.results.map((r, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#308795] mt-0.5 shrink-0" />
                      <span className="text-sm text-[#494546]">{r}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </Section>
);

/* Methodology */
const DSMethodology = () => (
  <Section bg="bg-[#465155]">
    <div className="max-w-3xl mb-14">
      <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">Methodology</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-3">Our SME Data Science Methodology</h2>
      <p className="text-[#D5CFC9] text-lg">Technology alone does not create impact. Methodology does.</p>
    </div>
    <div className="space-y-4">
      {DS_METHODOLOGY.map((m, i) => (
        <div key={i} className="bg-white/[0.06] border border-white/[0.08] rounded-xl p-6 transition-all duration-300 hover:bg-white/[0.1]">
          <div className="flex items-start gap-5">
            <span className="text-2xl font-extrabold text-[#7EA13E] shrink-0">{m.step}</span>
            <div>
              <h3 className="text-lg font-bold text-white mb-1">{m.title}</h3>
              <p className="text-sm text-[#D5CFC9] mb-2">{m.description}</p>
              <p className="text-xs font-semibold text-[#E5BD77] italic">{m.outcome}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </Section>
);

/* Governance */
const DSGovernance = () => (
  <Section bg="bg-white">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Governance</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{DS_GOVERNANCE.headline}</h2>
        <p className="text-[#6B6567] text-lg leading-relaxed mb-4">{DS_GOVERNANCE.description}</p>
        <p className="text-[#E95D54] font-semibold text-sm">{DS_GOVERNANCE.conclusion}</p>
      </div>
      <div className="mt-10 lg:mt-0 space-y-3">
        {DS_GOVERNANCE.capabilities.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className="flex items-center gap-4 bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-xl px-5 py-4 transition-all duration-300 hover:shadow-md">
              <div className="w-10 h-10 rounded-lg bg-[#308795]/[0.08] flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#308795]" />
              </div>
              <span className="text-sm font-medium text-[#494546]">{c.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  </Section>
);

/* ROI */
const DSROI = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="max-w-3xl mb-14">
      <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Proven Impact</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">{DS_ROI.headline}</h2>
      <p className="text-[#6B6567] text-lg leading-relaxed">{DS_ROI.description}</p>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
      <div className="bg-white border border-[#E0D1BB]/30 rounded-2xl p-6">
        <h3 className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-4">ROI Drivers</h3>
        <div className="space-y-2">{DS_ROI.drivers.map((d, i) => (<div key={i} className="flex items-start gap-3"><Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" /><span className="text-sm text-[#494546]">{d}</span></div>))}</div>
      </div>
      <div className="bg-white border border-[#E0D1BB]/30 rounded-2xl p-6">
        <h3 className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-4">Strategic Shifts</h3>
        <div className="space-y-2">{DS_ROI.strategicShifts.map((s, i) => (<div key={i} className="flex items-start gap-3"><Check className="w-4 h-4 text-[#308795] mt-0.5 shrink-0" /><span className="text-sm text-[#494546]">{s}</span></div>))}</div>
      </div>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {DS_ROI.positioning.map((p, i) => {
        const Icon = p.icon;
        return (
          <div key={i} className="bg-white border border-[#E0D1BB]/30 rounded-xl p-5 text-center transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            <div className="w-12 h-12 rounded-xl bg-[#E5BD77]/12 flex items-center justify-center mx-auto mb-3">
              <Icon className="w-6 h-6 text-[#A56A3E]" />
            </div>
            <p className="text-sm font-semibold text-[#2C2A2B]">{p.label}</p>
          </div>
        );
      })}
    </div>
  </Section>
);

/* Outcomes / Journey */
const DSOutcomes = () => (
  <Section bg="bg-white">
    <div className="text-center max-w-3xl mx-auto mb-12">
      <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">The Outcome</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">Your Data Science Journey</h2>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
      {DS_OUTCOMES.map((o, i) => (
        <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-xl p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-[#7EA13E]/[0.08] flex items-center justify-center mx-auto mb-2">
            <Check className="w-5 h-5 text-[#7EA13E]" />
          </div>
          <p className="text-xs font-semibold text-[#2C2A2B]">{o}</p>
        </div>
      ))}
    </div>
    <div className="max-w-2xl mx-auto">
      <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
        <div className="bg-[#E95D54]/10 border border-[#E95D54]/20 rounded-xl px-5 py-3 text-center"><p className="text-xs text-[#8A8385] mb-1">From</p><p className="text-sm font-bold text-[#E95D54]">{DS_JOURNEY.from}</p></div>
        <ArrowRight className="w-5 h-5 text-[#D5CFC9] rotate-0 sm:rotate-0" />
        <div className="bg-[#308795]/10 border border-[#308795]/20 rounded-xl px-5 py-3 text-center"><p className="text-xs text-[#8A8385] mb-1">To</p><p className="text-sm font-bold text-[#308795]">{DS_JOURNEY.to}</p></div>
        <ArrowRight className="w-5 h-5 text-[#D5CFC9]" />
        <div className="bg-[#7EA13E]/10 border border-[#7EA13E]/20 rounded-xl px-5 py-3 text-center"><p className="text-xs text-[#8A8385] mb-1">Beyond</p><p className="text-sm font-bold text-[#7EA13E]">{DS_JOURNEY.beyond}</p></div>
      </div>
    </div>
  </Section>
);

/* FAQ */
const DSFAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <Section bg="bg-[#FAFAF7]">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Frequently Asked Questions</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight">Data Science & Fabric FAQ</h2>
        </div>
        <div className="space-y-3">
          {DS_FAQ.map((item, i) => (
            <div key={i} className="border border-[#E0D1BB]/40 rounded-xl overflow-hidden bg-white transition-all duration-300">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[#FAFAF7] transition-colors">
                <span className="text-base font-semibold text-[#2C2A2B] pr-4">{item.q}</span>
                {open === i ? <ChevronUp className="w-5 h-5 text-[#308795] shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#D5CFC9] shrink-0" />}
              </button>
              {open === i && (<div className="px-6 pb-5 animate-fade-in"><p className="text-sm text-[#6B6567] leading-relaxed">{item.a}</p></div>)}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* CTA */
const DSContact = () => {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", company: "", website: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) { toast.error("Please fill in your first name, last name, and email."); return; }
    setLoading(true);
    try {
      const mp=[]; if(formData.company) mp.push(`Company: ${formData.company}`); if(formData.website) mp.push(`Website: ${formData.website}`); if(formData.message) mp.push(formData.message);
      await axios.post(`${API}/enquiries`, { name: `${formData.firstName} ${formData.lastName}`, email: formData.email, message: mp.join("\n") || null, selected_path: "Data Science & Analytics" });
      setSubmitted(true); toast.success("Thank you! We'll be in touch shortly.");
    } catch { toast.error("Something went wrong."); } finally { setLoading(false); }
  };
  return (
    <Section id="ds-contact" bg="bg-[#273519]">
      <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
        <div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">Ready to Unlock Your Data's Potential?</h2>
          <p className="text-[#b5c7a0] text-lg leading-relaxed mb-6">We understand your operational systems — because we often implemented them. That makes your data science journey faster, safer, and more aligned.</p>
          <p className="text-[#E5BD77] font-semibold italic">"36 years of business system experience. 24 years of Microsoft ERP expertise. Deep integration capability."</p>
        </div>
        <div className="mt-10 lg:mt-0">
          {!submitted ? (
            <div className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Book a Data Strategy Session</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="block text-sm font-medium text-[#b5c7a0] mb-1.5">First Name & Last Name</label><div className="grid grid-cols-2 gap-3"><input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#7EA13E] focus:ring-2 focus:ring-[#7EA13E]/10 transition-all" placeholder="First name" /><input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#7EA13E] focus:ring-2 focus:ring-[#7EA13E]/10 transition-all" placeholder="Last name" /></div></div>
                <div><label className="block text-sm font-medium text-[#b5c7a0] mb-1.5">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#7EA13E] focus:ring-2 focus:ring-[#7EA13E]/10 transition-all" placeholder="your@email.com" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-[#b5c7a0] mb-1.5">Name of Company</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#7EA13E] focus:ring-2 focus:ring-[#7EA13E]/10 transition-all" placeholder="Company name" /></div>
                  <div><label className="block text-sm font-medium text-[#b5c7a0] mb-1.5">Website</label><input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#7EA13E] focus:ring-2 focus:ring-[#7EA13E]/10 transition-all" placeholder="https://www.example.com" /></div>
                </div>
                <div><label className="block text-sm font-medium text-[#b5c7a0] mb-1.5">Message (Optional)</label><textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={3} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#7EA13E] focus:ring-2 focus:ring-[#7EA13E]/10 transition-all resize-none" placeholder="Tell us about your data challenges..." /></div>
                <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} dark />
                <Button type="submit" disabled={loading || !privacyConsent} className="w-full bg-[#7EA13E] hover:bg-[#6b8a34] text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#7EA13E]/20 text-base disabled:opacity-60">
                  {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : (<>Get Started<Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </form>
            </div>
          ) : (
            <div className="text-center py-12"><div className="w-16 h-16 rounded-full bg-[#7EA13E]/20 flex items-center justify-center mx-auto mb-5"><svg className="w-8 h-8 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div><h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3><p className="text-[#b5c7a0]">We'll be in touch to discuss your data strategy.</p></div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default function DataSciencePage() {
  return (
    <>
      <DSHero />
      <DSProblem />
      <DSFabric />
      <DSUseCases />
      <DSMethodology />
      <DSGovernance />
      <DSROI />
      <DSOutcomes />
      <DSFAQ />
      <DSContact />
    </>
  );
}
